// EmailAnalytics.jsx
import React, { useState, useEffect } from "react";
import {
  TrendingUp,
  AlertTriangle,
  Mail,
  Target,
  PieChart as PieChartIcon,
  BarChart3,
} from "lucide-react";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import api from "../../services/api";

const EmailAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [dateRange, setDateRange] = useState("30");

  useEffect(() => {
    loadAnalytics();
  }, [dateRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange));

      const response = await api.getEmailAnalyticsSummary({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });

      setSummary(response);
    } catch (error) {
      console.error("Failed to load analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = {
    priority: {
      urgent: "#EF4444",
      high: "#F59E0B",
      medium: "#3B82F6",
      low: "#10B981",
    },
    risk: {
      critical: "#DC2626",
      high: "#EF4444",
      medium: "#F59E0B",
      low: "#10B981",
    },
    sentiment: {
      very_positive: "#059669",
      positive: "#10B981",
      neutral: "#6B7280",
      negative: "#F59E0B",
      very_negative: "#EF4444",
    },
  };

  const transformChartData = (data) => {
    return Object.entries(data || {}).map(([name, value]) => ({
      name: name.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
      value,
    }));
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#1A1D23] border border-gray-700 rounded-lg p-3 shadow-xl">
          <p className="text-white font-medium mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const StatCard = ({ icon: Icon, title, value, iconBg }) => (
    <div className="bg-[#0C0E12] rounded-xl p-6 border border-gray-700/50 hover:border-gray-600/50 transition-colors">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-400 mb-2">{title}</p>
          <p className="text-3xl font-bold text-white">{value}</p>
        </div>
        <div className={`${iconBg} p-3 rounded-xl`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0C0E12] text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="min-h-screen bg-[#0C0E12] text-white font-inter">
        <div className="p-4 lg:p-6">
          <div className="text-center py-20">
            <p className="text-gray-400">No analytics data available</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C0E12] text-white font-inter">
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 text-white">
              Email Analytics
            </h1>
            <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
              Insights and trends from your email communications
            </p>
          </div>

          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 bg-[#13161B] border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={Mail}
            title="Total Emails"
            value={summary.totalEmails || 0}
            iconBg="bg-blue-600"
          />
          <StatCard
            icon={Target}
            title="Action Items"
            value={summary.actionItemsCount || 0}
            iconBg="bg-purple-600"
          />
          <StatCard
            icon={TrendingUp}
            title="Avg Sentiment"
            value={summary.averageSentimentScore?.toFixed(2) || "0.00"}
            iconBg="bg-green-600"
          />
          <StatCard
            icon={AlertTriangle}
            title="High Risk"
            value={
              (summary.byRiskLevel?.high || 0) +
              (summary.byRiskLevel?.critical || 0)
            }
            iconBg="bg-red-600"
          />
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Priority Distribution */}
          <div className="bg-[#0C0E12] border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-purple-400" />
              Priority Distribution
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={transformChartData(summary.byPriority)}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {transformChartData(summary.byPriority).map(
                    (entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          COLORS.priority[entry.name.toLowerCase()] || "#8B5CF6"
                        }
                      />
                    )
                  )}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Risk Level Distribution */}
          <div className="bg-[#0C0E12] border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Risk Level Distribution
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={transformChartData(summary.byRiskLevel)}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#374151"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  stroke="#9CA3AF"
                  fontSize={12}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  stroke="#9CA3AF"
                  fontSize={12}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                  {transformChartData(summary.byRiskLevel).map(
                    (entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          COLORS.risk[entry.name.toLowerCase()] || "#8B5CF6"
                        }
                      />
                    )
                  )}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Sentiment Distribution */}
          <div className="bg-[#0C0E12] border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              Sentiment Distribution
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={transformChartData(summary.bySentiment)}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#374151"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  stroke="#9CA3AF"
                  fontSize={12}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  stroke="#9CA3AF"
                  fontSize={12}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                  {transformChartData(summary.bySentiment).map(
                    (entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          COLORS.sentiment[
                            entry.name.toLowerCase().replace(/ /g, "_")
                          ] || "#8B5CF6"
                        }
                      />
                    )
                  )}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Category Distribution */}
          <div className="bg-[#0C0E12] border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-400" />
              Category Distribution
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={transformChartData(summary.byCategory)}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#374151"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  stroke="#9CA3AF"
                  fontSize={12}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  stroke="#9CA3AF"
                  fontSize={12}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" fill="#3B82F6" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Topics */}
        {summary.topTopics?.length > 0 && (
          <div className="bg-[#0C0E12] border border-gray-700 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-white mb-4">
              Top Topics
            </h3>
            <div className="flex flex-wrap gap-2">
              {summary.topTopics.map((topic) => (
                <span
                  key={topic.topic}
                  className="px-4 py-2 rounded-full text-sm font-medium bg-gray-700/50 text-gray-300 border border-gray-700"
                >
                  {topic.topic} ({topic.count})
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Compliance Flags */}
        {summary.complianceFlags &&
          Object.keys(summary.complianceFlags).length > 0 && (
            <div className="bg-[#0C0E12] border border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
                Compliance Alerts
              </h3>
              <div className="space-y-2">
                {Object.entries(summary.complianceFlags).map(
                  ([flag, count]) => (
                    <div
                      key={flag}
                      className="flex items-center justify-between p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg"
                    >
                      <span className="font-medium text-white">
                        {flag
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                      </span>
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-500/20 text-orange-300">
                        {count} occurrences
                      </span>
                    </div>
                  )
                )}
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default EmailAnalytics;
